/**
 * Report Chart Component
 *
 * Renders different chart types (bar, line, pie, summary) using Recharts library.
 * Supports dark mode and responsive layouts.
 */

import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { ReportResults } from '../../services/reportEngine';

interface ReportChartProps {
  results: ReportResults;
  chartType: 'bar' | 'line' | 'pie' | 'table' | 'summary';
}

const COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316'  // orange
];

export const ReportChart: React.FC<ReportChartProps> = ({
  results,
  chartType
}) => {
  if (!results || results.rows.length === 0) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">
            No data available to display chart
          </p>
        </div>
      </div>
    );
  }

  // Prepare data for charts
  const chartData = prepareChartData(results);

  switch (chartType) {
    case 'bar':
      return <BarChartComponent data={chartData} />;
    case 'line':
      return <LineChartComponent data={chartData} />;
    case 'pie':
      return <PieChartComponent data={chartData} />;
    case 'summary':
      return <SummaryCards results={results} />;
    default:
      return null; // Table is handled separately
  }
};

/**
 * Prepare data for charting
 */
function prepareChartData(results: ReportResults) {
  return results.rows.map(row => {
    const dataPoint: any = {};

    results.columns.forEach(col => {
      const value = row[col.field];

      // For numeric values, ensure they're numbers
      if (typeof value === 'number') {
        dataPoint[col.label] = value;
      } else if (typeof value === 'string' && !isNaN(Number(value))) {
        dataPoint[col.label] = Number(value);
      } else {
        dataPoint[col.label] = value;
      }
    });

    return dataPoint;
  });
}

/**
 * Bar Chart Component
 */
function BarChartComponent({ data }: { data: any[] }) {
  // Get all numeric columns for bars
  const numericKeys = Object.keys(data[0] || {}).filter(key =>
    typeof data[0][key] === 'number'
  );

  // Get category key (first non-numeric key)
  const categoryKey = Object.keys(data[0] || {}).find(key =>
    typeof data[0][key] !== 'number'
  );

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey={categoryKey}
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1e293b',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend />
          {numericKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={COLORS[index % COLORS.length]}
              radius={[8, 8, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Line Chart Component
 */
function LineChartComponent({ data }: { data: any[] }) {
  const numericKeys = Object.keys(data[0] || {}).filter(key =>
    typeof data[0][key] === 'number'
  );

  const categoryKey = Object.keys(data[0] || {}).find(key =>
    typeof data[0][key] !== 'number'
  );

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey={categoryKey}
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#9ca3af"
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1e293b',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend />
          {numericKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Pie Chart Component
 */
function PieChartComponent({ data }: { data: any[] }) {
  // For pie charts, we need name and value
  // Use first non-numeric field as name, first numeric field as value
  const categoryKey = Object.keys(data[0] || {}).find(key =>
    typeof data[0][key] !== 'number'
  );

  const valueKey = Object.keys(data[0] || {}).find(key =>
    typeof data[0][key] === 'number'
  );

  if (!categoryKey || !valueKey) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <p className="text-gray-600 dark:text-gray-400">Invalid data for pie chart</p>
      </div>
    );
  }

  // Transform data for pie chart
  const pieData = data.map(item => ({
    name: item[categoryKey],
    value: item[valueKey]
  }));

  return (
    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={true}
            label={(entry) => `${entry.name}: ${entry.value}`}
            outerRadius={120}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: '#1e293b',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#fff'
            }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Summary Cards Component
 */
function SummaryCards({ results }: { results: ReportResults }) {
  if (!results.summary || Object.keys(results.summary).length === 0) {
    return (
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <p className="text-gray-600 dark:text-gray-400">No summary data available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Object.entries(results.summary).map(([key, stat]) => (
        <div
          key={key}
          className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg p-6"
        >
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            {stat.label}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {typeof stat.value === 'number'
              ? stat.value.toLocaleString()
              : stat.value
            }
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase">
            {stat.function}
          </div>
        </div>
      ))}
    </div>
  );
}
